import {
  POS_PLAN_QUERY,
  POS_REQUIREMENT_TAB_COURSES,
  POS_REQUIREMENT_TAB_QUERY,
} from '@/constants/plan-of-study-constants';
import { STAFF_ROUTES } from '@/constants/staff-routes';

function normalizeQuery(search: string): URLSearchParams {
  const q = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  if (q.get('tab') === 'select-courses') {
    q.delete('tab');
    q.set(POS_REQUIREMENT_TAB_QUERY, POS_REQUIREMENT_TAB_COURSES);
  }
  return q;
}

function querySuffix(q: URLSearchParams): string {
  const s = q.toString();
  return s ? `?${s}` : '';
}

/**
 * Maps any legacy `/courses/plans-of-study/...` or `/staff/courses/plans-of-study/...`
 * URL to the canonical flat `/courses/plans/...` or `/courses/requirements/...` shape.
 */
export function legacyPlansOfStudyUrlToCanonical(pathname: string, search: string): string {
  const staffMatch = pathname.match(/^\/staff\/courses\/plans-of-study(\/(.*))?$/);
  const plainMatch = pathname.match(/^\/courses\/plans-of-study(\/(.*))?$/);
  const m = staffMatch ?? plainMatch;
  // Only for URLs handled by the legacy catch-all; do not use as a generic path normalizer (would hide typos).
  if (!m) {
    return `/${STAFF_ROUTES.plansOfStudy.list}`;
  }
  const prefix = staffMatch ? '/staff' : '';
  const rawRest = m[2] ?? '';
  const rest = rawRest.replace(/\/$/, '');
  const c = `${prefix}/courses`;
  const q = normalizeQuery(search);

  const reqUrl = (planId: string, reqId: string) => {
    const nq = new URLSearchParams(q);
    nq.set(POS_PLAN_QUERY, planId);
    return `${c}/requirements/${encodeURIComponent(reqId)}?${nq.toString()}`;
  };
  const createReqUrl = (planId: string) => {
    const nq = new URLSearchParams(q);
    nq.set(POS_PLAN_QUERY, planId);
    return `${c}/requirements/create?${nq.toString()}`;
  };

  if (!rest) {
    return `${c}/plans${querySuffix(q)}`;
  }
  if (rest === 'create') {
    return `${c}/plans/create${querySuffix(q)}`;
  }

  let x = /^detail\/([^/]+)\/requirement\/([^/]+)$/.exec(rest);
  if (x) {
    return reqUrl(x[1], x[2]);
  }
  x = /^detail\/([^/]+)\/requirement\/create$/.exec(rest);
  if (x) {
    return createReqUrl(x[1]);
  }
  x = /^detail\/([^/]+)$/.exec(rest);
  if (x) {
    return `${c}/plans/${encodeURIComponent(x[1])}${querySuffix(q)}`;
  }
  x = /^edit\/([^/]+)$/.exec(rest);
  if (x) {
    return `${c}/plans/${encodeURIComponent(x[1])}/edit${querySuffix(q)}`;
  }
  x = /^([^/]+)\/r\/([^/]+)$/.exec(rest);
  if (x) {
    return reqUrl(x[1], x[2]);
  }
  x = /^([^/]+)\/r\/create$/.exec(rest);
  if (x) {
    return createReqUrl(x[1]);
  }
  x = /^([^/]+)\/edit$/.exec(rest);
  if (x) {
    return `${c}/plans/${encodeURIComponent(x[1])}/edit${querySuffix(q)}`;
  }
  // `rest === 'create'` already returned above; remaining segments are treated as plan id.
  return `${c}/plans/${encodeURIComponent(rest)}${querySuffix(q)}`;
}
